/**
 * Orders — searchable order history with detail drawer, fiscal receipt reprint,
 * CSV export, and filters by status / forced-close / date range / order number.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmd, resolveAppLocale } from '../locale.js';
import type { Database } from '@cafe-pos/supabase';
import {
  fiscalReceiptTotal,
  LOYALTY_POINT_NAME,
  orderExpectedCharge,
  orderFiscalMismatch,
  orderItemsLoyaltyMismatch,
  orderLineItemsSubtotal,
  orderLoyaltyDiscountAmt,
  orderPaymentMismatch,
  orderSubtotalBeforeLoyalty,
  orderTipAmount,
  ORDER_PAYMENT_TOLERANCE_AMD,
  COMP_TYPES,
} from '@cafe-pos/shared';
import { fetchPaged } from '../fetchPaged.js';
import { supabase } from '../supabase.js';
import { printReceipt } from '../lib/receipt.js';
import { useCashierNames } from '../useCashierNames.js';
import { AlertTriangle, Download, Printer, X } from 'lucide-react';
import {
  Button,
  Callout,
  DateRange,
  FilterBar,
  FilterSearch,
  MultiSelect,
  PageHeader,
  SegmentedControl,
  resolveDateRange,
  type DateRangeValue,
  type MultiSelectMode,
} from '../ui/index.js';

type OrderRow = Database['public']['Tables']['orders']['Row'];
type OrderItemRow = Database['public']['Tables']['order_items']['Row'];
type PaymentRow = Database['public']['Tables']['payments']['Row'];
type DetailMember = Pick<
  Database['public']['Tables']['loyalty_members']['Row'],
  'member_code' | 'display_name'
>;
type CompRecipientRow = Pick<
  Database['public']['Tables']['staff']['Row'],
  'id' | 'name'
>;
type ProductOption = { id: string; name: string; active: boolean };

function parseCsvIds(raw: string | null): string[] {
  if (!raw) return [];
  return [...new Set(raw.split(',').map((s) => s.trim()).filter(Boolean))];
}

const fmt = (iso: string, locale: string) =>
  new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });

type PaySplit = { cash: number; card: number };

function fiscalNumberOf(fiscal: unknown): string {
  if (!fiscal || typeof fiscal !== 'object') return '';
  const rec = fiscal as { fiscal?: unknown; FISCAL?: unknown };
  const n = rec.fiscal ?? rec.FISCAL;
  return n == null || n === '' ? '' : String(n);
}

const LIVE_STORAGE_KEY = 'orders_live_updates';

export function OrdersView({
  clientId,
  onOpenShift,
}: {
  clientId: string | null;
  onOpenShift: (shiftId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string>('Orio');
  const cashierNames = useCashierNames(clientId);
  const cashierLabel = (id: number, userId?: string | null) => cashierNames.resolve(userId, id);

  // filters
  const hashParams = () => new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const todayStr = new Date().toISOString().slice(0, 10);
  const yesterdayStr = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  })();
  const [search, setSearch] = useState(() => hashParams().get('q') ?? '');
  const [statusFilter, setStatusFilter] = useState<string>(
    () => hashParams().get('status') ?? 'all',
  );
  const [forcedOnly, setForcedOnly] = useState(() => hashParams().get('forced') === '1');
  const [compTypeFilter, setCompTypeFilter] = useState(() => hashParams().get('compType') ?? 'all');
  const [compRecipientFilter, setCompRecipientFilter] = useState(
    () => hashParams().get('compRecipient') ?? '',
  );
  const [compRecipients, setCompRecipients] = useState<CompRecipientRow[]>([]);
  const [range, setRange] = useState<DateRangeValue>(() => {
    const from = hashParams().get('from');
    const to = hashParams().get('to');
    if (from || to) return { preset: 'custom', from: from ?? yesterdayStr, to: to ?? todayStr };
    return { preset: 'custom', from: yesterdayStr, to: todayStr };
  });
  const { from: fromDate, to: toDate } = resolveDateRange(range);
  const [shiftId, setShiftId] = useState(() => hashParams().get('shift') ?? '');
  const [payFilter, setPayFilter] = useState<'all' | 'cash' | 'card'>(() => {
    const raw = hashParams().get('pay');
    return raw === 'cash' || raw === 'card' ? raw : 'all';
  });
  const [orderPayMethods, setOrderPayMethods] = useState<Map<string, Set<string>>>(new Map());
  const [productFilter, setProductFilter] = useState<string[]>(() => parseCsvIds(hashParams().get('items')));
  const [productFilterMode, setProductFilterMode] = useState<MultiSelectMode>(() =>
    hashParams().get('itemsMode') === 'exclude' ? 'exclude' : 'include',
  );
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [ordersWithItems, setOrdersWithItems] = useState<Set<string> | null>(null);

  // pagination
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);

  // detail drawer
  const [detailOrder, setDetailOrder] = useState<OrderRow | null>(null);
  const [detailItems, setDetailItems] = useState<OrderItemRow[]>([]);
  const [detailPayments, setDetailPayments] = useState<PaymentRow[]>([]);
  const [detailMember, setDetailMember] = useState<DetailMember | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [ordersWithPayments, setOrdersWithPayments] = useState<Set<string>>(new Set());
  const [paymentTotals, setPaymentTotals] = useState<Map<string, number>>(new Map());
  const [paymentSplits, setPaymentSplits] = useState<Map<string, PaySplit>>(new Map());

  async function load() {
    if (!supabase) return;
    const db = supabase;
    setLoading(true);
    setError(null);
    const buildQuery = () => {
      let q = db
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });
      if (statusFilter !== 'all') q = q.eq('status', statusFilter);
      if (forcedOnly) q = q.eq('forced_close', true);
      if (compTypeFilter !== 'all') q = q.eq('comp_type', compTypeFilter);
      if (compRecipientFilter.trim()) q = q.eq('comp_recipient_id', compRecipientFilter.trim());
      if (fromDate) q = q.gte('created_at', new Date(fromDate).toISOString());
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        q = q.lte('created_at', end.toISOString());
      }
      if (shiftId.trim()) q = q.eq('shift_id', shiftId.trim());
      return q;
    };
    let loaded: OrderRow[];
    try {
      loaded = await fetchPaged<OrderRow>((from, to) => buildQuery().range(from, to));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setLoading(false);
      return;
    }
    setOrders(loaded);
    // Fetch which paid orders have at least one payment row.
    // Chunk the id list — a single .in() of every paid order blows the request URL.
    const orderIds = loaded.map((o) => o.id);
    if (orderIds.length > 0) {
      const totals = new Map<string, number>();
      const methods = new Map<string, Set<string>>();
      const splits = new Map<string, PaySplit>();
      const CHUNK = 150;
      for (let i = 0; i < orderIds.length; i += CHUNK) {
        const { data: payData, error: payErr } = await db
          .from('payments')
          .select('order_id, amount, method')
          .in('order_id', orderIds.slice(i, i + CHUNK));
        if (payErr) {
          setError(payErr.message);
          setLoading(false);
          return;
        }
        for (const p of payData ?? []) {
          const amount = Number(p.amount);
          const method = p.method === 'pos' ? 'card' : p.method;
          totals.set(p.order_id, (totals.get(p.order_id) ?? 0) + amount);
          const set = methods.get(p.order_id) ?? new Set<string>();
          set.add(method);
          methods.set(p.order_id, set);
          const split = splits.get(p.order_id) ?? { cash: 0, card: 0 };
          if (method === 'cash') split.cash += amount;
          else if (method === 'card') split.card += amount;
          splits.set(p.order_id, split);
        }
      }
      setOrdersWithPayments(new Set(totals.keys()));
      setPaymentTotals(totals);
      setOrderPayMethods(methods);
      setPaymentSplits(splits);
    } else {
      setOrdersWithPayments(new Set());
      setPaymentTotals(new Map());
      setOrderPayMethods(new Map());
      setPaymentSplits(new Map());
    }
    if (productFilter.length > 0 && loaded.length > 0) {
      const match = new Set<string>();
      const CHUNK = 150;
      const loadedIds = loaded.map((o) => o.id);
      for (let i = 0; i < loadedIds.length; i += CHUNK) {
        const chunk = loadedIds.slice(i, i + CHUNK);
        const { data: itemRows } = await supabase
          .from('order_items')
          .select('order_id')
          .in('order_id', chunk)
          .in('product_id', productFilter);
        for (const row of itemRows ?? []) match.add(row.order_id);
      }
      setOrdersWithItems(match);
    } else {
      setOrdersWithItems(productFilter.length > 0 ? new Set() : null);
    }
    setLoading(false);
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, forcedOnly, compTypeFilter, compRecipientFilter, range, shiftId, productFilter]);

  useEffect(() => {
    if (!supabase) return;
    void supabase
      .from('staff')
      .select('id, name')
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true })
      .then(({ data }) => setCompRecipients((data ?? []) as CompRecipientRow[]));
  }, [clientId]);

  useEffect(() => {
    if (!supabase || !clientId) return;
    void supabase
      .from('products')
      .select('id, name, active')
      .eq('client_id', clientId)
      .order('name')
      .then(({ data }) => setProducts((data ?? []) as ProductOption[]));
  }, [clientId]);

  const recipientNameById = useMemo(() => {
    const map = new Map(compRecipients.map((r) => [r.id, r.name]));
    return (id: string | null | undefined) => (id ? (map.get(id) ?? t('common.unknown')) : null);
  }, [compRecipients]);

  // Keep filters in sync with the URL hash so links/back-forward restore state.
  useEffect(() => {
    const p = new URLSearchParams();
    if (search.trim()) p.set('q', search.trim());
    if (statusFilter !== 'all') p.set('status', statusFilter);
    if (forcedOnly) p.set('forced', '1');
    if (compTypeFilter !== 'all') p.set('compType', compTypeFilter);
    if (compRecipientFilter.trim()) p.set('compRecipient', compRecipientFilter.trim());
    if (fromDate) p.set('from', fromDate);
    if (toDate) p.set('to', toDate);
    if (shiftId.trim()) p.set('shift', shiftId.trim());
    if (payFilter !== 'all') p.set('pay', payFilter);
    if (productFilter.length) {
      p.set('items', productFilter.join(','));
      if (productFilterMode === 'exclude') p.set('itemsMode', 'exclude');
    }
    const qs = p.toString();
    const newHash = qs ? `#/orders?${qs}` : '#/orders';
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
  }, [search, statusFilter, forcedOnly, compTypeFilter, compRecipientFilter, range, shiftId, payFilter, productFilter, productFilterMode]);

  // Live updates: when the POS syncs order changes to the cloud, reload the list.
  const loadRef = useRef(load);
  loadRef.current = load;
  const [live, setLive] = useState(false);
  const [liveEnabled, setLiveEnabled] = useState(
    () => localStorage.getItem(LIVE_STORAGE_KEY) !== '0',
  );
  useEffect(() => {
    if (!supabase || !clientId || !liveEnabled) {
      setLive(false);
      return;
    }
    const client = supabase;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const reload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void loadRef.current(), 400);
    };
    const channel = client
      .channel(`orders-live-${clientId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders', filter: `client_id=eq.${clientId}` },
        reload,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'payments', filter: `client_id=eq.${clientId}` },
        reload,
      )
      .subscribe((status) => setLive(status === 'SUBSCRIBED'));
    return () => {
      if (timer) clearTimeout(timer);
      void client.removeChannel(channel);
    };
  }, [clientId, liveEnabled]);

  useEffect(() => {
    if (!supabase || !clientId) return;
    void supabase
      .from('clients')
      .select('name')
      .eq('id', clientId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.name) setClientName(data.name);
      });
  }, [clientId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (payFilter !== 'all' && !orderPayMethods.get(o.id)?.has(payFilter)) return false;
      if (ordersWithItems) {
        const hasItem = ordersWithItems.has(o.id);
        if (productFilterMode === 'exclude' ? hasItem : !hasItem) return false;
      }
      if (!q) return true;
      if (String(o.number).includes(q)) return true;
      if (o.id.toLowerCase().includes(q)) return true;
      if ((o.partner_tin ?? '').toLowerCase().includes(q)) return true;
      if ((o.close_note ?? '').toLowerCase().includes(q)) return true;
      const rec = recipientNameById(o.comp_recipient_id);
      if (rec && rec.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [orders, search, recipientNameById, payFilter, orderPayMethods, ordersWithItems, productFilterMode]);

  // Reset to first page whenever the filter result set changes.
  const filteredKey =
    filtered.length +
    statusFilter +
    search +
    forcedOnly +
    compTypeFilter +
    compRecipientFilter +
    fromDate +
    toDate +
    shiftId +
    payFilter +
    productFilter.join(',') +
    productFilterMode;
  useEffect(() => {
    setPage(0);
  }, [filteredKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagedOrders = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Totals across the current filter set: all orders regardless of status
  // (so filtering to open/void/refunded still shows a meaningful sum), plus
  // the paid subtotal when the two differ.
  const filteredTotals = useMemo(() => {
    let all = 0;
    let paid = 0;
    for (const o of filtered) {
      all += Number(o.total);
      if (o.status === 'paid') paid += Number(o.total);
    }
    return { all, paid };
  }, [filtered]);

  async function openDetail(o: OrderRow) {
    if (!supabase) return;
    setDetailOrder(o);
    setDetailItems([]);
    setDetailPayments([]);
    setDetailMember(null);
    setDetailLoading(true);
    const memberQuery = o.loyalty_member_id
      ? supabase
          .from('loyalty_members')
          .select('member_code, display_name')
          .eq('id', o.loyalty_member_id)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null });
    const [{ data: items }, { data: payments }, { data: member }] = await Promise.all([
      supabase.from('order_items').select('*').eq('order_id', o.id),
      supabase.from('payments').select('*').eq('order_id', o.id),
      memberQuery,
    ]);
    setDetailItems(items ?? []);
    setDetailPayments(payments ?? []);
    setDetailMember(member ?? null);
    setDetailLoading(false);
  }

  function exportCsv() {
    const statusLabel = (status: string) => {
      if (status === 'open') return t('orders.statusOpen');
      if (status === 'paid') return t('orders.statusPaid');
      if (status === 'void') return t('orders.statusVoid');
      if (status === 'refunded') return t('orders.statusRefunded');
      return status;
    };
    const paymentLabel = (status: string) => {
      if (status === 'paid') return t('orders.statusPaid');
      if (status === 'unpaid') return t('orders.paymentUnpaid');
      if (status === 'partial') return t('orders.paymentPartial');
      return status;
    };
    const closeReasonLabel = (reason: string | null) => {
      const key = (
        {
          mistake: 'sales.reasonMistake',
          system_error: 'sales.reasonSystemError',
          free: 'sales.reasonFree',
          other: 'sales.reasonOther',
        } as Record<string, string>
      )[reason ?? ''];
      return key ? t(key) : (reason ?? '');
    };
    const money = (n: number | null | undefined) =>
      n == null || !Number.isFinite(n) ? '' : String(Math.round(n));
    const header = [
      t('orders.colNumber'),
      t('orders.colTime'),
      t('orders.colStatus'),
      t('orders.colPayment'),
      t('orders.cash'),
      t('orders.card'),
      t('orders.colTotal'),
      t('orders.cardTip'),
      t('orders.colAmount'),
      t('orders.fiscalNumber'),
      t('orders.fiscalTotal'),
      t('orders.cashier'),
      t('orders.colTin'),
      t('orders.shift'),
      t('orders.colForced'),
      t('orders.closeReason'),
      t('orders.compType'),
      t('orders.compRecipient'),
    ];
    const rows = filtered.map((o) => {
      const split = paymentSplits.get(o.id);
      const methods: string[] = [];
      if (split && split.cash > 0) methods.push(t('orders.cash'));
      if (split && split.card > 0) methods.push(t('orders.card'));
      const fiscalTotal = fiscalReceiptTotal(o.fiscal);
      return [
        o.number,
        fmt(o.created_at, locale),
        statusLabel(o.status),
        methods.length ? methods.join(', ') : paymentLabel(o.payment_status),
        split ? money(split.cash) : '',
        split ? money(split.card) : '',
        money(o.total),
        money(orderTipAmount(o.tip_amount)),
        money(paymentTotals.get(o.id)),
        fiscalNumberOf(o.fiscal),
        fiscalTotal == null ? '' : money(fiscalTotal),
        cashierLabel(o.cashier_id, o.cashier_user_id),
        o.partner_tin ?? '',
        o.shift_id ?? '',
        o.forced_close ? t('common.yes') : '',
        closeReasonLabel(o.close_reason),
        o.comp_type ? t(`compTypes.${o.comp_type}`) : '',
        recipientNameById(o.comp_recipient_id) ?? '',
      ];
    });
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function reprint(o: OrderRow) {
    if (!supabase) return;
    const [{ data: items }, { data: payments }] = await Promise.all([
      supabase.from('order_items').select('*').eq('order_id', o.id),
      supabase.from('payments').select('*').eq('order_id', o.id),
    ]);
    printReceipt({
      order: o,
      items: items ?? [],
      payments: payments ?? [],
      clientName,
    });
  }

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('orders.title')}
        subtitle={
          <>
            {t('orders.subtitleFiltered', { filtered: filtered.length, total: orders.length })}
            <span style={{ marginLeft: 10, color: 'var(--text)', fontWeight: 600 }}>
              {t('orders.subtitleTotal', { amount: AMD(filteredTotals.all) })}
            </span>
            {filteredTotals.paid !== filteredTotals.all && (
              <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>
                {t('orders.subtitlePaid', { amount: AMD(filteredTotals.paid) })}
              </span>
            )}
            {live && (
              <span className="live-badge" title={t('orders.live')}>
                {t('orders.live')}
              </span>
            )}
          </>
        }
        actions={
          <>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: 'var(--text-muted)',
              }}
            >
              <input
                type="checkbox"
                checked={liveEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  setLiveEnabled(on);
                  localStorage.setItem(LIVE_STORAGE_KEY, on ? '1' : '0');
                }}
              />
              {t('orders.liveUpdates')}
            </label>
            <Button variant="secondary" size="sm" icon={<Download className="ui-i" />} onClick={exportCsv} disabled={filtered.length === 0}>
              {t('common.exportCsv')}
            </Button>
          </>
        }
      />

      {error && <Callout tone="danger">{error}</Callout>}

      {(() => {
        const missing = filtered.filter(
          (o) => o.status === 'paid' && !ordersWithPayments.has(o.id),
        );
        if (missing.length === 0) return null;
        return (
          <div className="warning-banner" style={{ marginBottom: 12 }}>
            <AlertTriangle className="ui-i" /> {t('orders.missingPayments', { count: missing.length })}
            {missing.length <= 5 && (
              <span style={{ marginLeft: 6, fontWeight: 400 }}>
                — #{missing.map((o) => o.number).join(', #')}
              </span>
            )}
          </div>
        );
      })()}

      <div className="panel">
        <FilterBar className="add-form" style={{ flexWrap: 'wrap', marginBottom: 0 }}>
          <FilterSearch
            placeholder={t('orders.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 200, maxWidth: 'none' }}
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">{t('orders.allStatuses')}</option>
            <option value="open">{t('orders.statusOpen')}</option>
            <option value="paid">{t('orders.statusPaid')}</option>
            <option value="void">{t('orders.statusVoid')}</option>
            <option value="refunded">{t('orders.statusRefunded')}</option>
          </select>
          <select value={compTypeFilter} onChange={(e) => setCompTypeFilter(e.target.value)}>
            <option value="all">{t('orders.allCompTypes')}</option>
            {COMP_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`compTypes.${type}`)}
              </option>
            ))}
          </select>
          <select
            value={compRecipientFilter}
            onChange={(e) => setCompRecipientFilter(e.target.value)}
          >
            <option value="">{t('orders.allRecipients')}</option>
            {compRecipients.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <MultiSelect
            ariaLabel={t('orders.menuItems')}
            placeholder={t('orders.menuItems')}
            searchPlaceholder={t('orders.searchMenu')}
            options={products.map((p) => ({
              value: p.id,
              label: p.active === false ? `${p.name}${t('orders.inactiveSuffix')}` : p.name,
            }))}
            value={productFilter}
            onChange={setProductFilter}
            mode={productFilterMode}
            onModeChange={setProductFilterMode}
          />
          <SegmentedControl
            size="sm"
            ariaLabel={t('orders.paymentType')}
            value={payFilter}
            onChange={setPayFilter}
            options={[
              { value: 'all', label: t('orders.all') },
              { value: 'cash', label: t('orders.cash') },
              { value: 'card', label: t('orders.card') },
            ]}
          />
          <DateRange value={range} onChange={setRange} size="sm" />
          <input
            placeholder={t('orders.shiftId')}
            value={shiftId}
            onChange={(e) => setShiftId(e.target.value)}
            style={{ width: 140 }}
          />
          {shiftId.trim() && (
            <button
              className="btn-ghost btn-sm"
              onClick={() => setShiftId('')}
              title={t('orders.clearShift')}
            >
              <X className="ui-i" /> {t('orders.shift')}
            </button>
          )}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: 'var(--text-muted)',
            }}
          >
            <input
              type="checkbox"
              checked={forcedOnly}
              onChange={(e) => setForcedOnly(e.target.checked)}
            />
            {t('orders.forcedOnly')}
          </label>
        </FilterBar>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : (
          <table className="table table-cards">
            <thead>
              <tr>
                <th>{t('orders.colNumber')}</th>
                <th>{t('orders.colTime')}</th>
                <th>{t('orders.colStatus')}</th>
                <th>{t('orders.colPayment')}</th>
                <th>{t('orders.colTotal')}</th>
                <th>{t('orders.colCashierShift')}</th>
                <th>{t('orders.colTin')}</th>
                <th>{t('orders.colComp')}</th>
                <th>{t('orders.colForced')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pagedOrders.map((o) => (
                <tr
                  key={o.id}
                  className={o.forced_close ? 'forced-close-row clickable-row' : 'clickable-row'}
                  onClick={() => void openDetail(o)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="table-cards-title">
                    <button className="link" onClick={() => void openDetail(o)}>
                      {fmtDay(o.created_at)} · #{o.number}
                    </button>
                  </td>
                  <td data-label={t('orders.colTime')} style={{ color: 'var(--text-muted)' }}>{fmt(o.created_at, locale)}</td>
                  <td data-label={t('orders.colStatus')}>
                    {t(
                      o.status === 'open'
                        ? 'orders.statusOpen'
                        : o.status === 'paid'
                          ? 'orders.statusPaid'
                          : o.status === 'void'
                            ? 'orders.statusVoid'
                            : o.status === 'refunded'
                              ? 'orders.statusRefunded'
                              : 'orders.statusOpen',
                      { defaultValue: o.status },
                    )}
                  </td>
                  <td data-label={t('orders.colPayment')}>
                    {o.status === 'paid' && !ordersWithPayments.has(o.id) ? (
                      <span
                        style={{ color: 'var(--warning, #b45309)', fontWeight: 600, fontSize: 12 }}
                        title={t('orders.missingTitle')}
                      >
                        <AlertTriangle className="ui-i" /> {t('orders.missing')}
                      </span>
                    ) : o.status === 'paid' &&
                      orderPaymentMismatch(
                        paymentTotals.get(o.id) ?? 0,
                        Number(o.total),
                        o.tip_amount,
                      ) ? (
                      <span
                        style={{ color: 'var(--warning, #b45309)', fontWeight: 600, fontSize: 12 }}
                        title={
                          orderTipAmount(o.tip_amount) > 0
                            ? t('orders.payMismatchTip', {
                                paid: AMD(paymentTotals.get(o.id) ?? 0),
                                expected: AMD(orderExpectedCharge(Number(o.total), o.tip_amount)),
                                due: AMD(o.total),
                                tip: AMD(orderTipAmount(o.tip_amount)),
                              })
                            : t('orders.payMismatch', {
                                paid: AMD(paymentTotals.get(o.id) ?? 0),
                                expected: AMD(orderExpectedCharge(Number(o.total), o.tip_amount)),
                                due: AMD(o.total),
                              })
                        }
                      >
                        <AlertTriangle className="ui-i" /> {t('orders.pay')}
                      </span>
                    ) : o.status === 'paid' &&
                      ordersWithPayments.has(o.id) &&
                      orderFiscalMismatch(paymentTotals.get(o.id) ?? 0, o.fiscal) ? (
                      <span
                        style={{ color: 'var(--danger)', fontWeight: 600, fontSize: 12 }}
                        title={t('orders.fiscalMismatch', { charged: AMD(paymentTotals.get(o.id) ?? 0), fiscal: AMD(fiscalReceiptTotal(o.fiscal) ?? 0) })}
                      >
                        <AlertTriangle className="ui-i" /> {t('orders.fiscal')}
                      </span>
                    ) : o.payment_status === 'paid' ? (
                      t('orders.statusPaid')
                    ) : o.payment_status === 'unpaid' ? (
                      t('orders.paymentUnpaid')
                    ) : o.payment_status === 'partial' ? (
                      t('orders.paymentPartial')
                    ) : (
                      o.payment_status
                    )}
                  </td>
                  <td data-label={t('orders.colTotal')}>
                    {AMD(o.total)}
                    {orderLoyaltyDiscountAmt(o) > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {t('orders.inclLoyalty', { amount: AMD(orderLoyaltyDiscountAmt(o)) })}
                      </div>
                    )}
                    {orderTipAmount(o.tip_amount) > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {t('orders.plusTip', { amount: AMD(orderTipAmount(o.tip_amount)) })}
                      </div>
                    )}
                    {o.status === 'paid' &&
                      ordersWithPayments.has(o.id) &&
                      orderFiscalMismatch(paymentTotals.get(o.id) ?? 0, o.fiscal) && (
                        <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 2 }}>
                          {t('orders.fiscal')} {AMD(fiscalReceiptTotal(o.fiscal) ?? 0)}
                        </div>
                      )}
                  </td>
                  <td data-label={t('orders.colCashierShift')}>
                    {o.shift_id ? (
                      <button
                        className="link"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenShift(o.shift_id!);
                        }}
                        title={t('orders.goToShift')}
                      >
                        {cashierLabel(o.cashier_id, o.cashier_user_id)}
                      </button>
                    ) : (
                      cashierLabel(o.cashier_id, o.cashier_user_id)
                    )}
                  </td>
                  <td data-label={t('orders.colTin')} style={{ color: 'var(--text-muted)' }}>{o.partner_tin ?? t('common.emptyDash')}</td>
                  <td data-label={t('orders.colComp')} style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    {o.comp_type ? (
                      <>
                        <span className="badge">{t(`compTypes.${o.comp_type}`, { defaultValue: o.comp_type })}</span>
                        {recipientNameById(o.comp_recipient_id) && (
                          <div style={{ marginTop: 2 }}>{recipientNameById(o.comp_recipient_id)}</div>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td data-label={t('orders.colForced')}>
                    {o.forced_close ? (
                      <span style={{ color: 'var(--danger)', fontWeight: 600 }} title={o.close_reason ?? undefined}>
                        <AlertTriangle className="ui-i" />{' '}
                        {(() => {
                          const key = (
                            {
                              mistake: 'sales.reasonMistake',
                              system_error: 'sales.reasonSystemError',
                              free: 'sales.reasonFree',
                              other: 'sales.reasonOther',
                            } as Record<string, string>
                          )[o.close_reason ?? ''];
                          return key ? t(key) : (o.close_reason ?? '');
                        })()}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-subtle)' }}>—</span>
                    )}
                  </td>
                  <td className="table-cards-actions">
                    <button
                      className="btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        void reprint(o);
                      }}
                      title={t('orders.reprintTitle')}
                    >
                      <Printer className="ui-i" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      textAlign: 'center',
                      padding: '28px 20px',
                      color: 'var(--text-subtle)',
                    }}
                  >
                    {t('orders.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {filtered.length > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px 12px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              className="btn-ghost btn-sm"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              ← Prev
            </button>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {page + 1} / {Math.ceil(filtered.length / PAGE_SIZE)}
            </span>
            <button
              className="btn-ghost btn-sm"
              disabled={(page + 1) * PAGE_SIZE >= filtered.length}
              onClick={() => setPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {detailOrder && (
        <div className="modal-overlay" onClick={() => setDetailOrder(null)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                Order #{detailOrder.number}
                <span
                  style={{
                    color: 'var(--text-muted)',
                    fontWeight: 400,
                    marginLeft: 12,
                    fontSize: 14,
                  }}
                >
                  {fmt(detailOrder.created_at, locale)}
                </span>
              </h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost btn-sm" onClick={() => void reprint(detailOrder)}>
                  <Printer className="ui-i" /> {t('orders.reprint')}
                </button>
                <button className="btn-icon" onClick={() => setDetailOrder(null)}>
                  ×
                </button>
              </div>
            </div>

            <div className="detail-grid">
              <div>
                <strong>{t('orders.colStatus')}</strong> {detailOrder.status}
              </div>
              <div>
                <strong>{t('orders.colPayment')}</strong> {detailOrder.payment_status}
              </div>
              <div>
                <strong>{t('orders.cashier')}</strong>{' '}
                {cashierLabel(detailOrder.cashier_id, detailOrder.cashier_user_id)}
              </div>
              {detailOrder.closed_by_user_id &&
                detailOrder.closed_by_user_id !== detailOrder.cashier_user_id && (
                  <div>
                    <strong>{t('orders.closedBy')}</strong>{' '}
                    {cashierLabel(detailOrder.cashier_id, detailOrder.closed_by_user_id)}
                  </div>
                )}
              <div>
                <strong>{t('orders.shift')}</strong>{' '}
                {detailOrder.shift_id ? (
                  <button
                    className="link"
                    onClick={() => onOpenShift(detailOrder.shift_id!)}
                    title={t('orders.goToShift')}
                  >
                    {t('orders.openShift')}
                  </button>
                ) : (
                  '—'
                )}
              </div>
              <div>
                <strong>{t('orders.colTin')}</strong> {detailOrder.partner_tin ?? '—'}
              </div>
              {detailOrder.forced_close && (
                <div style={{ gridColumn: '1 / -1', color: 'var(--danger)' }}>
                  <strong><AlertTriangle className="ui-i" /> {t('orders.forceClosed')}</strong> — {detailOrder.close_reason ?? t('orders.noReason')}
                  {detailOrder.close_note ? ` · ${detailOrder.close_note}` : ''}
                </div>
              )}
              {(detailOrder.comp_type || detailOrder.comp_recipient_id) && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <strong>{t('orders.colComp')}</strong>{' '}
                  {detailOrder.comp_type
                    ? t(`compTypes.${detailOrder.comp_type}`, { defaultValue: detailOrder.comp_type })
                    : t('common.emptyDash')}
                  {detailOrder.comp_recipient_id && (
                    <> · {recipientNameById(detailOrder.comp_recipient_id) ?? t('orders.unknownRecipient')}</>
                  )}
                </div>
              )}
            </div>

            {detailLoading ? (
              <div className="empty-state">{t('common.loading')}</div>
            ) : (
              <>
                {(() => {
                  const itemsSubtotal = orderLineItemsSubtotal(detailItems);
                  const loyaltyDiscount = orderLoyaltyDiscountAmt(detailOrder);
                  const coinsRedeemed = Math.max(0, Number(detailOrder.loyalty_coins_redeemed) || 0);
                  const amountDue = Number(detailOrder.total);
                  const tip = orderTipAmount(detailOrder.tip_amount);
                  const charged = orderExpectedCharge(amountDue, detailOrder.tip_amount);
                  const hasLoyalty =
                    !!detailOrder.loyalty_member_id || loyaltyDiscount > 0 || coinsRedeemed > 0;

                  return (
                    <>
                      {hasLoyalty && (
                        <>
                          <h3 style={{ marginTop: 16 }}>{t('orders.loyalty')}</h3>
                          <div className="detail-grid" style={{ marginBottom: 8 }}>
                            <div>
                              <strong>{t('orders.member')}</strong>{' '}
                              {detailMember ? (
                                <>
                                  {detailMember.display_name ? `${detailMember.display_name} · ` : ''}
                                  <code>#{detailMember.member_code}</code>
                                </>
                              ) : detailOrder.loyalty_member_id ? (
                                <span className="muted">{t('orders.removedMember')}</span>
                              ) : (
                                '—'
                              )}
                            </div>
                            {loyaltyDiscount > 0 && (
                              <div>
                                <strong>{t('orders.discount')}</strong> −{AMD(loyaltyDiscount)}
                              </div>
                            )}
                            {coinsRedeemed > 0 && (
                              <div>
                                <strong>{LOYALTY_POINT_NAME} redeemed</strong> {coinsRedeemed}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      <h3 style={{ marginTop: hasLoyalty ? 8 : 16 }}>{t('orders.totals')}</h3>
                      <div className="detail-grid" style={{ marginBottom: 8 }}>
                        <div>
                          <strong>{t('orders.itemsSubtotal')}</strong> {AMD(itemsSubtotal)}
                        </div>
                        {loyaltyDiscount > 0 && (
                          <div>
                            <strong>{t('orders.loyaltyDiscount')}</strong> −{AMD(loyaltyDiscount)}
                          </div>
                        )}
                        <div>
                          <strong>{t('orders.amountDue')}</strong> {AMD(amountDue)}
                        </div>
                        {tip > 0 && (
                          <div>
                            <strong>{t('orders.cardTip')}</strong> {AMD(tip)}
                          </div>
                        )}
                        <div>
                          <strong>{t('orders.charged')}</strong> {AMD(charged)}
                        </div>
                      </div>
                    </>
                  );
                })()}

                <h3 style={{ marginTop: 16 }}>{t('orders.items')}</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('orders.colName')}</th>
                      <th>{t('orders.colQty')}</th>
                      <th>{t('orders.colUnitPrice')}</th>
                      <th>{t('orders.colDiscount')}</th>
                      <th>{t('orders.colLineTotal')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailItems.map((it) => {
                      const mods = Array.isArray(it.modifiers)
                        ? (it.modifiers as { name?: string; priceDelta?: number }[])
                        : [];
                      return (
                        <tr key={it.id}>
                          <td>
                            {it.name}
                            {mods.length > 0 && (
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                {mods
                                  .map((m) =>
                                    m.priceDelta ? `${m.name} (+${AMD(m.priceDelta)})` : m.name,
                                  )
                                  .join(', ')}
                              </div>
                            )}
                          </td>
                          <td>
                            {it.quantity} {it.unit}
                          </td>
                          <td>{AMD(it.unit_price)}</td>
                          <td>{it.discount ? AMD(it.discount) : t('common.emptyDash')}</td>
                          <td>{AMD(it.unit_price * it.quantity - (it.discount ?? 0))}</td>
                        </tr>
                      );
                    })}
                    {detailItems.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ color: 'var(--text-subtle)' }}>
                          {t('orders.noItems')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <h3 style={{ marginTop: 16 }}>{t('orders.payments')}</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('orders.colMethod')}</th>
                      <th>{t('orders.colAmount')}</th>
                      <th>{t('orders.colStatus')}</th>
                      <th>{t('orders.colTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailPayments.map((p) => (
                      <tr key={p.id}>
                        <td>{p.method}</td>
                        <td>{AMD(p.amount)}</td>
                        <td>{p.status}</td>
                        <td style={{ color: 'var(--text-muted)' }}>{fmt(p.created_at, locale)}</td>
                      </tr>
                    ))}
                    {detailPayments.length === 0 && (
                      <tr>
                        <td colSpan={4} style={{ color: 'var(--text-subtle)' }}>
                          {t('orders.noPayments')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {(() => {
                  if (detailPayments.length === 0) return null;
                  const payTotal = detailPayments.reduce((s, p) => s + Number(p.amount), 0);
                  const amountDue = Number(detailOrder.total);
                  const tip = orderTipAmount(detailOrder.tip_amount);
                  const expected = orderExpectedCharge(amountDue, detailOrder.tip_amount);
                  const itemsSubtotal = orderLineItemsSubtotal(detailItems);
                  const loyaltyDiscount = orderLoyaltyDiscountAmt(detailOrder);
                  const payGap = payTotal - expected;
                  const itemsGap = itemsSubtotal - orderSubtotalBeforeLoyalty(amountDue, loyaltyDiscount);
                  const payMismatch = Math.abs(payGap) >= ORDER_PAYMENT_TOLERANCE_AMD;
                  const itemsMismatch = orderItemsLoyaltyMismatch(
                    itemsSubtotal,
                    amountDue,
                    loyaltyDiscount,
                  );
                  if (!payMismatch && !itemsMismatch) {
                    if (tip <= 0 && loyaltyDiscount <= 0) return null;
                    return (
                      <div
                        style={{
                          marginTop: 8,
                          marginBottom: 0,
                          fontSize: 13,
                          color: 'var(--text-muted)',
                        }}
                      >
                        {loyaltyDiscount > 0 && (
                          <>
                            Items {AMD(itemsSubtotal)} − loyalty {AMD(loyaltyDiscount)} = due{' '}
                            {AMD(amountDue)}.{' '}
                          </>
                        )}
                        Payment {AMD(payTotal)}
                        {tip > 0
                          ? ` includes ${AMD(tip)} card tip (${AMD(amountDue)} due + ${AMD(tip)} tip).`
                          : ` matches amount due.`}
                      </div>
                    );
                  }
                  return (
                    <>
                      {payMismatch && (
                        <div className="warning-banner" style={{ marginTop: 8, marginBottom: 0 }}>
                          <AlertTriangle className="ui-i" /> Payment total ({AMD(payTotal)}) doesn't match expected charge (
                          {AMD(expected)}
                          {tip > 0 ? `: ${AMD(amountDue)} due + ${AMD(tip)} tip` : ''}) —
                          difference: {AMD(Math.abs(payGap))}
                        </div>
                      )}
                      {itemsMismatch && (
                        <div
                          className="warning-banner"
                          style={{ marginTop: payMismatch ? 8 : 8, marginBottom: 0 }}
                        >
                          <AlertTriangle className="ui-i" /> Items subtotal ({AMD(itemsSubtotal)}) doesn't match amount due (
                          {AMD(amountDue)}
                          {loyaltyDiscount > 0
                            ? ` + ${AMD(loyaltyDiscount)} loyalty`
                            : ''}
                          ) — difference: {AMD(Math.abs(itemsGap))}
                        </div>
                      )}
                    </>
                  );
                })()}

                {detailOrder.fiscal && (
                  <>
                    <h3 style={{ marginTop: 16 }}>{t('orders.fiscalTitle')}</h3>
                    {detailPayments.length > 0 &&
                      (() => {
                        const payTotal = detailPayments.reduce((s, p) => s + Number(p.amount), 0);
                        const fiscalTotal = fiscalReceiptTotal(detailOrder.fiscal);
                        if (
                          fiscalTotal == null ||
                          !orderFiscalMismatch(payTotal, detailOrder.fiscal)
                        ) {
                          return (
                            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                              {t('orders.fiscalMatches', { fiscal: AMD(fiscalTotal ?? 0), charged: AMD(payTotal) })}
                            </div>
                          );
                        }
                        const gap = fiscalTotal - payTotal;
                        return (
                          <div className="warning-banner" style={{ marginBottom: 8 }}>
                            ⚠ <strong>{t('orders.fiscalMismatchTitle')}</strong> — charged {AMD(payTotal)} but HDM
                            fiscal receipt shows {AMD(fiscalTotal)} (
                            {gap > 0 ? '+' : ''}
                            {AMD(gap)} on receipt). The printed fiscal total may not match what
                            was collected.
                          </div>
                        );
                      })()}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                        gap: 8,
                        marginBottom: 8,
                        fontSize: 13,
                      }}
                    >
                      <div>
                        <strong>{t('orders.fiscalNumber')}</strong>{' '}
                        {String(
                          (detailOrder.fiscal as { fiscal?: string }).fiscal ??
                            (detailOrder.fiscal as { FISCAL?: string }).FISCAL ??
                            '—',
                        )}
                      </div>
                      <div>
                        <strong>{t('orders.fiscalTotal')}</strong>{' '}
                        {AMD(fiscalReceiptTotal(detailOrder.fiscal) ?? 0)}
                      </div>
                      {typeof (detailOrder.fiscal as { change?: number }).change === 'number' && (
                        <div>
                          <strong>{t('orders.change')}</strong>{' '}
                          {AMD(Number((detailOrder.fiscal as { change: number }).change))}
                        </div>
                      )}
                    </div>
                    <pre
                      style={{
                        background: 'var(--bg-muted, #f5f5f5)',
                        padding: 12,
                        borderRadius: 6,
                        fontSize: 12,
                        overflow: 'auto',
                        maxHeight: 240,
                      }}
                    >
                      {JSON.stringify(detailOrder.fiscal, null, 2)}
                    </pre>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
